import React, { type PropsWithChildren, useEffect, useMemo } from 'react'
import { useAsync } from 'react-use'
import { IntlProvider } from 'react-intl'
import { generateIntl, loadMessages, defaultMessages } from 'lang/intl'

import { useLiveDataQuery } from '../global/blocs/useLiveData'

const DEFAULT_LOCALE = 'en'

const localesMap = {
  en_US: 'en',
  en_GB: 'en',
  cz_CZ: 'cs',
  da_DK: 'da',
  de_DE: 'de',
  es_ES: 'es',
  fi_FI: 'fi',
  fr_FR: 'fr',
  hu_HU: 'hu',
  it_IT: 'it',
  ja_JP: 'ja',
  ko_KR: 'ko',
  lt_LT: 'lt',
  no_NO: 'no',
  nl_NL: 'nl',
  pl_PL: 'pl',
  pt_PT: 'pt',
  ru_RU: 'ru',
  sv_SE: 'sv',
  tr_TR: 'tr',
  vi_VN: 'vi',
  zh_CN: 'zh-Hans',
  zh_TW: 'zh-Hant',
}

const IntlProviderWrapper = ({ children }: PropsWithChildren<{}>) => {
  const { data } = useLiveDataQuery()

  const mappedLocale = useMemo(() => {
    return localesMap[data?.locale as keyof typeof localesMap] ?? DEFAULT_LOCALE
  }, [data?.locale])

  const { value: messages = defaultMessages } = useAsync(async () => {
    return loadMessages(mappedLocale)
  }, [mappedLocale])

  useEffect(() => {
    generateIntl({ locale: mappedLocale, messages })
  }, [messages, mappedLocale])

  if (!data) return null

  return (
    <IntlProvider
      locale={mappedLocale}
      messages={messages}
      defaultLocale={DEFAULT_LOCALE}
    >
      {children}
    </IntlProvider>
  )
}

export { IntlProviderWrapper, DEFAULT_LOCALE }
